import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  useGetStrategicAdTypesQuery, 
  useCreateStrategicAdTypeMutation, 
  useUpdateStrategicAdTypeMutation,
  useDeleteStrategicAdTypeMutation
} from '../../../store/api/adminApi';
import PageLoader from '../../../components/PageLoader';
import { showSuccess, showError } from '../../../utils/toast';
import { confirmAction } from '../../../utils/confirm';
import { useLanguage } from '../../../context/LanguageContext';

interface StrategicType {
  id?: number;
  title: string;
  title_ar: string;
  description: string;
  description_ar: string;
  key: string;
  status: boolean;
}

const KEY_OPTIONS = [
  { value: 'main_banner', label: 'Main Banner' },
  { value: 'secondary_banner', label: 'Secondary Banner' },
  { value: 'popup_ad', label: 'Popup Ad' },
  { value: 'push_notification', label: 'Push Notification' },
  { value: 'chat_header_ad', label: 'Chat Header Ad' },
];

const AdminStrategicAdTypeForm: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { id } = useParams<{ id: string }>();
  
  // Fetch list of strategic ad types. 
  // We assume fetching all (per_page 100) to show in the list.
  // If filtering by ad_type_id is supported, we pass it.
  const { data, isLoading, refetch } = useGetStrategicAdTypesQuery({ per_page: 100 });
  
  const [createAdType, { isLoading: isCreating }] = useCreateStrategicAdTypeMutation();
  const [updateAdType, { isLoading: isUpdating }] = useUpdateStrategicAdTypeMutation();
  const [deleteAdType, { isLoading: isDeleting }] = useDeleteStrategicAdTypeMutation();

  const [types, setTypes] = useState<StrategicType[]>([]);

  useEffect(() => {
    if (data?.data?.data) {
      const fetchedTypes = data.data.data.map((t: any) => ({
        id: t.id,
        title: t.title || '',
        title_ar: t.title_ar || '',
        description: t.description || '',
        description_ar: t.description_ar || '',
        key: t.key || '',
        status: t.status === 1 || t.status === true
      }));
      if (fetchedTypes.length > 0) {
        setTypes(fetchedTypes);
      } else {
        // If no types exist, start with one empty form
        setTypes([{ title: '', title_ar: '', description: '', description_ar: '', key: '', status: true }]);
      }
    } else if (data && !data.data) {
        // Handle case where data might be directly an array (unlikely but possible)
        // or empty.
        setTypes([{ title: '', title_ar: '', description: '', description_ar: '', key: '', status: true }]);
    }
  }, [data]);

  const handleTypeChange = (index: number, field: keyof StrategicType, value: any) => {
    const newTypes = [...types];
    // @ts-ignore
    newTypes[index] = { ...newTypes[index], [field]: value };
    setTypes(newTypes);
  };

  const addType = () => {
    setTypes([...types, { title: '', title_ar: '', description: '', description_ar: '', key: '', status: true }]);
  };

  const removeType = async (index: number) => {
    const type = types[index];
    
    if (type.id) {
        const isConfirmed = await confirmAction(t('Are you sure?'), t('You want to delete this strategic ad type?'));
        if (!isConfirmed) return;

        try {
            await deleteAdType(type.id).unwrap();
            showSuccess(t('Strategic ad type deleted successfully'));
            const newTypes = types.filter((_, i) => i !== index);
            setTypes(newTypes.length > 0 ? newTypes : [{ title: '', title_ar: '', description: '', description_ar: '', key: '', status: true }]);
        } catch (error) {
            showError(t('Failed to delete strategic ad type'));
        }
    } else {
        const newTypes = types.filter((_, i) => i !== index);
        setTypes(newTypes.length > 0 ? newTypes : [{ title: '', title_ar: '', description: '', description_ar: '', key: '', status: true }]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate
    const invalidType = types.find(t => 
        !t.title.trim() || !t.title_ar.trim() || !t.description.trim() || !t.description_ar.trim()
    );

    if (invalidType) {
        showError(t('All fields (English & Arabic) are required for all types.'));
        return;
    }
    
    try {
      const promises = types.map(type => {
        const payload = {
          title: type.title.trim(),
          title_ar: type.title_ar.trim(),
          description: type.description.trim(),
          description_ar: type.description_ar.trim(),
          key: type.key,
          status: type.status
        };

        if (type.id) {
          return updateAdType({ id: type.id, data: payload }).unwrap();
        } else {
          return createAdType(payload).unwrap();
        }
      });

      await Promise.all(promises);
      showSuccess(t('Strategic ad types saved successfully!'));
      navigate('/admin/ad-types');
    } catch (err: any) {
      if (err?.data?.errors) {
        const errorMessages = Object.values(err.data.errors).flat().join(', ');
        showError(errorMessages || err?.data?.message || t('Validation failed'));
      } else {
        showError(err?.data?.message || t('Failed to save strategic ad types. Please try again.'));
      }
    }
  };

  if (isLoading) {
    return <PageLoader />;
  }

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                {t('Manage Strategic Ad Types')}
              </h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/dashboard'); }} className="text-muted text-hover-primary">{t('Dashboard')}</a>
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-400 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">
                  <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/ad-types'); }} className="text-muted text-hover-primary">{t('Ad Types')}</a>
                </li>
              </ul>
            </div>
          </div>
        </div>
        
        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-xxl">
            <form onSubmit={handleSubmit} className="form fv-plugins-bootstrap5 fv-plugins-framework">
                <div className="row">
                    <div className="col-12">
                      {types.map((type, index) => (
                        <div key={index} className="card bg-white shadow mb-4 border">
                          <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center mb-4">
                              <div className="d-flex align-items-center">
                                <h4 className="card-title m-0 me-4">{t('Strategic Type')} #{index + 1}</h4>
                                <div className="form-check form-switch form-check-custom form-check-solid">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        checked={type.status}
                                        onChange={(e) => handleTypeChange(index, 'status', e.target.checked)}
                                    />
                                    <label className="form-check-label fw-semibold text-gray-500">
                                        {type.status ? t('Active') : t('Inactive')}
                                    </label>
                                </div>
                              </div>
                              <button
                                type="button"
                                className="btn btn-icon btn-active-color-danger btn-sm"
                                onClick={() => removeType(index)}
                                title={t('Remove Type')}
                              >
                                <span className="svg-icon svg-icon-2">
                                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <rect opacity="0.5" x="7.05025" y="15.5356" width="12" height="2" rx="1" transform="rotate(-45 7.05025 15.5356)" fill="currentColor"></rect>
                                    <rect x="8.46447" y="7.05029" width="12" height="2" rx="1" transform="rotate(45 8.46447 7.05029)" fill="currentColor"></rect>
                                  </svg>
                                </span>
                              </button>
                            </div>
                            
                            <div className="row g-9">
                              <div className="col-md-6 fv-row">
                                <label className="required fs-6 fw-semibold mb-2">{t('Title')}</label>
                                <input
                                  type="text"
                                  className="form-control form-control-solid"
                                  placeholder={t('Type Title')}
                                  value={type.title}
                                  onChange={(e) => handleTypeChange(index, 'title', e.target.value)}
                                  required
                                />
                              </div>
                              <div className="col-md-6 fv-row">
                                <label className="required fs-6 fw-semibold mb-2">{t('Title Arabic')}</label>
                                <input
                                  type="text"
                                  className="form-control form-control-solid"
                                  placeholder={t('Type Title (Arabic)')}
                                  value={type.title_ar}
                                  onChange={(e) => handleTypeChange(index, 'title_ar', e.target.value)}
                                  required
                                />
                              </div>
                              <div className="col-md-6 fv-row">
                                <label className="required fs-6 fw-semibold mb-2">{t('Description')}</label>
                                <textarea name="description" id="" className="form-control form-control-solid"
                                placeholder={t('Type Description')} value={type.description} onChange={(e) => handleTypeChange(index, 'description', e.target.value)}></textarea>

                              </div>
                              <div className="col-md-6 fv-row">
                                <label className="required fs-6 fw-semibold mb-2">{t('Description Arabic')}</label>
                                 <textarea name="description" id="" className="form-control form-control-solid"
                                placeholder={t('Type Description (Arabic)')} value={type.description_ar} onChange={(e) => handleTypeChange(index, 'description_ar', e.target.value)}></textarea>

                              </div>
                              <div className="col-md-6 fv-row">
                                <label className="fs-6 fw-semibold mb-2">{t('Key')}</label>
                                <select
                                    className="form-select form-select-solid"
                                    value={type.key}
                                    onChange={(e) => handleTypeChange(index, 'key', e.target.value)}
                                >
                                    <option value="">{t('Select Key')}</option>
                                    {KEY_OPTIONS.map(opt => (
                                        <option key={opt.value} value={opt.value}>{t(opt.label)}</option>
                                    ))}
                                </select>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                </div>

                <div className="d-flex justify-content-between mt-5">
                    <button type="button" className="btn btn-light-primary" onClick={addType}>
                        <span className="svg-icon svg-icon-2 me-2">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect opacity="0.5" x="11" y="18" width="12" height="2" rx="1" transform="rotate(-90 11 18)" fill="currentColor"></rect>
                                <rect x="6" y="11" width="12" height="2" rx="1" fill="currentColor"></rect>
                            </svg>
                        </span>
                        {t('Add Another Type')}
                    </button>

                    <div>
                        <button type="button" className="btn btn-light me-3" onClick={() => navigate('/admin/ad-types')}>
                            {t('Discard')}
                        </button>
                        <button type="submit" className="btn btn-primary" disabled={isCreating || isUpdating || isDeleting}>
                            {(isCreating || isUpdating || isDeleting) ? (
                                <span className="indicator-progress" style={{ display: 'block' }}>
                                    {t('Please wait...')} <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                                </span>
                            ) : (
                                <span className="indicator-label">{t('Save Changes')}</span>
                            )}
                        </button>
                    </div>
                </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminStrategicAdTypeForm;
